import { Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../components/Header";
import { fetchServiceStartCoordinates } from "../../services/serviceService";
import { getDriversByBoss } from "../../services/driverService";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const parsePoint = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    const [lng, lat] = raw.map(Number);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  if (raw.coordinates && Array.isArray(raw.coordinates)) {
    const [lng, lat] = raw.coordinates.map(Number);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  const lat = Number(raw.lat ?? raw.latitude);
  const lng = Number(raw.lng ?? raw.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const aggregatePoints = (points, precision = 3) => {
  const buckets = new Map();
  points.forEach((p) => {
    if (!p) return;
    const key = `${p.lat.toFixed(precision)}|${p.lng.toFixed(precision)}`;
    const current = buckets.get(key) || { ...p, count: 0 };
    current.count += 1;
    buckets.set(key, current);
  });
  return Array.from(buckets.values());
};

const getColorForCount = (count, max) => {
  const ratio = max ? count / max : 0;
  if (ratio < 0.33) return "#4ade80";
  if (ratio < 0.66) return "#facc15";
  return "#f87171";
};

const ResizeInvalidator = () => {
  const map = useMap();
  useEffect(() => {
    if (!map) return undefined;
    const resize = () => map.invalidateSize();
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [map]);
  return null;
};

const Heatmap = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const start = new Date();
    start.setFullYear(today.getFullYear() - 5);
    const toIso = (d) => d.toISOString().slice(0, 10);
    return {
      startDate: toIso(start),
      endDate: toIso(today),
      drivers: "ALL",
    };
  });

  const loadDrivers = useCallback(async () => {
    try {
      const list = await getDriversByBoss();
      setDrivers(list || []);
    } catch (err) {
      console.error("No se pudieron cargar los conductores", err);
    }
  }, []);

  const loadData = useCallback(
    async (activeFilters = filters) => {
      setLoading(true);
      setError("");
      try {
        const driverIds =
          activeFilters.drivers === "ALL" || !activeFilters.drivers
            ? []
            : [activeFilters.drivers];
        const coords = await fetchServiceStartCoordinates({
          startDate: activeFilters.startDate,
          endDate: activeFilters.endDate,
          drivers: driverIds,
        });
        const parsed = Array.isArray(coords)
          ? coords.map((c) => ({
              id: c.id || c._id || c.externalId,
              posicionInicio: c.posicionInicio || c,
            }))
          : [];
        setData(parsed.filter((p) => p.posicionInicio));
      } catch (err) {
        setError(err.message || "No se pudieron cargar las coordenadas");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    loadDrivers();
    loadData();
  }, [loadDrivers, loadData]);

  const aggregated = useMemo(
    () =>
      aggregatePoints(
        data
          .map((c) => parsePoint(c.posicionInicio))
          .filter(Boolean),
        3
      ),
    [data]
  );
  const maxCount = useMemo(
    () => aggregated.reduce((max, p) => Math.max(max, p.count || 0), 0),
    [aggregated]
  );

  const palette = {
    cardBg: isLight ? "#ffffff" : "#0f172a",
    cardBorder: isLight ? "#e5e7eb" : "#1f2937",
    text: isLight ? "#0f172a" : "#e2e8f0",
    label: isLight ? "#475569" : "#cbd5e1",
    btn: isLight ? "#2563EB" : "#3B82F6",
    btnHover: isLight ? "#1D4ED8" : "#2563EB",
  };
  const tileUrl = isLight
    ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    : "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
  const tileAttribution = isLight
    ? '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
    : '&copy; <a href="https://carto.com/attributions">CARTO</a> & OSM';
  const mapFilter = isLight ? "none" : "brightness(1.25) contrast(1.05) saturate(1.05)";

  const center = aggregated[0]
    ? [aggregated[0].lat, aggregated[0].lng]
    : [40.4168, -3.7038];

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        m: 0,
        px: { xs: 2, md: 3 },
        py: { xs: 1.5, md: 3 },
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Header title="Mapa de calor" subtitle="Concentración de servicios por origen" />

      <Box
        sx={{
          flex: 1,
          height: { xs: "100%", md: "calc(100vh - 150px)" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 3 },
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 2,
            border: `1px solid ${palette.cardBorder}`,
            backgroundColor: palette.cardBg,
            width: { xs: "100%", md: 320 },
            flexShrink: 0,
            maxHeight: { xs: "32vh", md: "100%" },
            overflow: "auto",
          }}
        >
          <Typography variant="h6" fontWeight={700} color={palette.text} gutterBottom>
            Resumen
          </Typography>
          <Box display="flex" flexDirection="column" gap={1.25} mb={2}>
            <TextField
              label="Desde"
              type="date"
              size="small"
              value={filters.startDate}
              onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: alpha(palette.text, 0.3) },
                  "&.Mui-focused fieldset": { borderColor: palette.text },
                },
                "& .MuiOutlinedInput-input": { color: palette.text },
                "& .MuiInputLabel-root": { color: palette.label },
                "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
              }}
              inputProps={{ onClick: (e) => e.target.showPicker?.() }}
            />
            <TextField
              label="Hasta"
              type="date"
              size="small"
              value={filters.endDate}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: alpha(palette.text, 0.3) },
                  "&.Mui-focused fieldset": { borderColor: palette.text },
                },
                "& .MuiOutlinedInput-input": { color: palette.text },
                "& .MuiInputLabel-root": { color: palette.label },
                "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
              }}
              inputProps={{ onClick: (e) => e.target.showPicker?.() }}
            />
            <TextField
              label="Conductores"
              select
              size="small"
              value={filters.drivers}
              onChange={(e) => {
                setFilters((f) => ({ ...f, drivers: e.target.value }));
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: alpha(palette.text, 0.3) },
                  "&:hover fieldset": { borderColor: palette.text },
                  "&.Mui-focused fieldset": { borderColor: palette.text },
                },
                "& .MuiOutlinedInput-input": { color: palette.text },
                "& .MuiInputLabel-root": { color: palette.label },
                "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
              }}
            >
              <MenuItem value="ALL">Todos</MenuItem>
              {drivers.map((d) => (
                <MenuItem key={d._id || d.id} value={d._id || d.id}>
                  {d.name || d.fullName || d.username || d.email || d._id}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => loadData(filters)}
              disabled={loading}
              sx={{
                backgroundColor: palette.btn,
                color: "white",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { backgroundColor: palette.btnHover },
              }}
            >
              Aplicar filtros
            </Button>
          </Box>
          {loading ? (
            <Box display="flex" alignItems="center" gap={1.5}>
              <CircularProgress size={22} />
              <Typography color={palette.label}>Cargando coordenadas...</Typography>
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <Box display="grid" gridTemplateColumns="repeat(2, minmax(0,1fr))" gap={1}>
              <Stat label="Puntos únicos" value={aggregated.length} palette={palette} />
              <Stat label="Servicios" value={data.length} palette={palette} />
              <Stat label="Máx por zona" value={maxCount || "-"} palette={palette} />
            </Box>
          )}
          <Box mt={2}>
            <Typography variant="body2" color={palette.label}>
              Visualiza dónde se originan más servicios. Cada círculo representa una zona
              (aprox. 100 m) y su tamaño/color crece con el número de inicios en esa área.
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 1,
            borderRadius: 2,
            border: `1px solid ${palette.cardBorder}`,
            backgroundColor: palette.cardBg,
            flex: 1,
            minWidth: 0,
            height: "100%",
            minHeight: { xs: 240, md: 280 },
            overflow: "hidden",
          }}
        >
          {loading ? (
            <Box height="100%" display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box height="100%" display="flex" alignItems="center" justifyContent="center">
              <Typography color="error">{error}</Typography>
            </Box>
          ) : (
            <MapContainer
              center={center}
              zoom={12}
              style={{
                width: "100%",
                height: "100%",
                minHeight: 320,
                borderRadius: 12,
                overflow: "hidden",
                filter: mapFilter,
              }}
              scrollWheelZoom
            >
              <ResizeInvalidator />
              <TileLayer attribution={tileAttribution} url={tileUrl} />
              {aggregated.map((point) => {
                const radius = Math.min(6 + point.count * 1.2, 25);
                const color = getColorForCount(point.count, maxCount);
                return (
                  <CircleMarker
                    key={`${point.lat}-${point.lng}`}
                    center={[point.lat, point.lng]}
                    radius={radius}
                    pathOptions={{
                      color,
                      fillColor: color,
                      fillOpacity: 0.35 + Math.min(point.count / (maxCount || 1), 0.5),
                      weight: 1,
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -4]} opacity={1}>
                      <div>
                        <div><strong>Servicios:</strong> {point.count}</div>
                        <div>
                          <strong>Zona:</strong> {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
                        </div>
                      </div>
                    </Tooltip>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

function Stat({ label, value, palette }) {
  return (
    <Box
      sx={{
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 1.5,
        p: 1.25,
      }}
    >
      <Typography variant="caption" sx={{ color: palette.label, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default Heatmap;
